<template>
  <b-overlay
    variant="white"
    spinner-variant="primary"
    blur="0"
    opacity=".75"
    rounded="sm"
    :show="isLoading"
  >
    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col cols="12">
            <vue-good-table
              :columns="columns"
              :rows="curriculumVitaeCertificates"
              :pagination-options="paginationOptions"
              :line-numbers="true"
            >
              <div
                slot="emptystate"
                style="text-align: center; font-weight: bold"
              >
                Không có bản ghi nào !
              </div>
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field === 'studentCertificateType'">
                  {{ getCertificateName(props.row.studentCertificateType) }}
                </span>
                <span v-else-if="props.column.field === 'studentCertificateStatus'">
                  {{ getStatusName(props.row.studentCertificateStatus) }}
                </span>
                <span v-else-if="props.column.field === 'action'">
                  <b-button
                    v-if="readable"
                    variant="primary"
                    class="btn-icon"
                    size="sm"
                    @click="onDownload(props.row)"
                  >
                    <feather-icon icon="DownloadIcon" />
                  </b-button>
                  <b-button
                    v-if="updatable"
                    variant="success"
                    class="btn-icon ml-1"
                    size="sm"
                    @click="onAccept(props.row, 1)"
                  >
                    <feather-icon icon="CheckIcon" />
                  </b-button>
                  <b-button
                    v-if="updatable"
                    variant="danger"
                    class="btn-icon ml-1"
                    size="sm"
                    @click="onAccept(props.row, -1)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BCard, BCardBody, BCol, BOverlay, BRow, BButton
} from 'bootstrap-vue'
import { getUser } from '@/auth/utils'
import { VueGoodTable } from 'vue-good-table'
import { hasPermissionForResource } from '@/utils'
import { PermissionCode, ResourceCode } from '@/const/code'
import { CERTIFICATE_STATUS, CERTIFICATE_TYPES } from '@/const/status'
import api from './api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'StudentCertificate',
  components: {
    BCol,
    BRow,
    BCard,
    BCardBody,
    BOverlay,
    VueGoodTable,
    BButton,
  },
  props: {
    curriculumVitaeCertificates: {
      type: Array,
      default: () => [],
    },
    studentId: {
      type: Number,
      default: undefined,
    }
  },
  data() {
    return {
      isLoading: false,
      user: getUser(),
      columns: [
        {
          label: 'Tên sinh viên',
          field: 'studentName',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Loại chứng chỉ',
          field: 'studentCertificateType',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          field: 'studentCertificateStatus',
          label: 'Trạng thái',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          field: 'action',
          label: '',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],
      paginationOptions: {
        enabled: false,
      },
      filter: {
        studentId: null,
        status: null,
        currentPage: 1,
        itemsPerPage: 1000,
      },
      currentData: undefined
    }
  },
  computed: {
    readable() {
      return hasPermissionForResource(PermissionCode.READ, ResourceCode.STUDENT_CERTIFICATE_FILE)
    },
    updatable() {
      return hasPermissionForResource(PermissionCode.UPDATE, ResourceCode.STUDENT_CERTIFICATE)
    },
    isCreated() {
      return this.currentData === undefined || this.currentData === null
    },
  },
  methods: {
    getCertificateName(id) {
      return CERTIFICATE_TYPES.find(status => status.value === id).label
    },
    getStatusName(id) {
      return CERTIFICATE_STATUS.find(status => status.value === id).label
    },
    async onAccept(data, status) {
      this.$swal({
        title: `Bạn chắc chắn muốn xác nhận trạng thái chứng chỉ`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '<i class="fa-solid fa-check"></i> Đồng ý',
        cancelButtonText: '<i class="fa-solid fa-times"></i> Hủy',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          this.isLoading = true
          try {
            const res = await api.updateData({
              studentCertificateId: data.studentCertificateId,
              status: status
            })
            if (res) {
              const { isSuccessful, message } = res
              if (isSuccessful) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: message,
                    icon: 'CheckIcon',
                    variant: 'success',
                  },
                })
                this.$emit('succeed')
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: message,
                    icon: 'XCircleIcon',
                    variant: 'danger',
                  },
                })
              }
            }
          } catch (e) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: `[${e.code}] ${e.message}`,
                icon: 'XCircleIcon',
                variant: 'danger',
              },
            })
          } finally {
            this.isLoading = false
          }
        }
      })
    },
    async onDownload(data) {
      this.isLoading = true
      try {
        const objectPath = data.studentCertificateFiles[0].code
        const res = await api.downloadDocument({ objectPath })
        if ( res ) {
          window.open(res, '_blank')
        }
      } catch ( e ) {
        this.showToast(`[${e.code}] ${e.message}`, 'XCircleIcon', 'danger')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table';
</style>
